// Trilingual content (EN / TR / NL) for the Ahmet Şaşkın portfolio site.

export const translations = {
    en: {
        nav: {
            home: "Home",
            about: "About",
            work: "My Work",
            offer: "Creative Services",
            pricing: "Pricing",
            contact: "Contact",
        },
        brand: {
            name: "Ahmet Saskin",
            tagline: "Wall Art & Murals",
        },
        hero: {
            kicker: "Mural Artist — est. portfolio",
            line1: "Walls with",
            line2Italic: "soul.",
            line3: "",
            line4Italic: "",
            subhead:
                "Custom mural art, wall painting, and visual storytelling for homes, cafés, restaurants, hotels, offices and public spaces.",
            intro:
                "Ahmet Saskin creates hand-painted wall art and murals that bring identity, emotion, and atmosphere into every space.",
            ctaPrimary: "View My Work",
            ctaSecondary: "Get in Touch",
            chips: [
                "Custom murals",
                "Canvas painting",
                "Interior & exterior",
                "Commissioned art",
                "Private & commercial",
            ],
            scroll: "Scroll to begin",
        },
        marquee: [
            "Hand-painted",
            "Custom murals",
            "Interior & exterior",
            "Commercial commissions",
            "Editorial wall art",
            "Concept to execution",
        ],
        about: {
            eyebrow: "01 — About",
            title: "About Ahmet Saskin",
            body:
                "Ahmet Saskin is a creative professional, mural artist and visual creator based in Amsterdam — the founder of Ahmetography, specializing in hand-painted murals and custom wall art. After a long career in the online payments industry, the perfect opportunity presented itself in 2023, and he decided to follow his true passion for art.",
            body2:
                "Since then, Ahmet has been independently developing his craft through self-directed study of painting techniques, materials and styles — with a focus on mural design and visual storytelling. That journey was earlier shaped by an art course at the Gerrit Rietveld Academie in 2010–2011.\n\nWhether it's a private home or a commercial space — a café, restaurant, office, hotel or outdoor setting — Ahmet creates custom murals tailored to each client's vision. He can design artwork that complements an existing interior, or bring a specific idea to life as a unique piece that reflects the character of the space. For him, a mural is more than decoration — it's a chance to transform a room, evoke emotion, and create a lasting connection with the people who experience it.",
            quote:
                "Every wall has a story. Together, let's paint yours.",
            quoteAttr: "— Ahmet Saskin",
            metaLeft: "Working since",
            metaLeftValue: "2024",
            metaRight: "Based in",
            metaRightValue: "Amsterdam, NL",
        },
        stats: {
            eyebrow: "02 — In Numbers",
            items: [
                { num: "20+", label: "Completed walls" },
                { num: "100%", label: "Hand-painted" },
                { num: "—", label: "Indoor & outdoor" },
            ],
            footnote:
                "Residential and commercial commissions across Türkiye, from intimate interiors to large-scale public-facing walls.",
        },
        portfolio: {
            eyebrow: "03 — Selected Works",
            title: "My Work",
            subtitle:
                "A curated archive of recent murals, painted walls and commissioned compositions.",
            featuredLabel: "Featured",
            seeAll: "See All Work",
            filters: {
                all: "All",
                mural: "Murals",
                canvas: "Canvas paintings",
            },
            cardMeta: {
                location: "Location",
                surface: "Surface",
                size: "Dimensions",
                medium: "Medium",
            },
        },
        services: {
            eyebrow: "04 — Creative Services",
            title: "Creative Services",
            subtitle:
                "Six ways to bring hand-painted art into your space — for private homes and commercial venues alike: cafés, restaurants, hotels, offices and beyond.",
            items: [
                {
                    title: "Custom Murals",
                    body: "Hand-painted murals designed specifically for your wall, your story, and your space.",
                },
                {
                    title: "Canvas Painting",
                    body: "Original hand-painted artworks on canvas, available in various sizes — from statement pieces to large-scale commissions.",
                },
                {
                    title: "Site-Specific Installations",
                    body: "Artwork created specifically for the architecture and atmosphere of a space, ensuring every piece feels integrated and unique.",
                },
                {
                    title: "Decorative Wall Art",
                    body: "Layered patterns, textures and motifs created to elevate hospitality and retail interiors.",
                },
                {
                    title: "Commercial Mural and Art Project",
                    body: "Creative solutions for hospitality, retail, offices, cafés and other commercial environments looking for distinctive artwork.",
                },
                {
                    title: "Creative Consultation",
                    body: "Concept development, color direction, placement ideas and artistic planning for interior and exterior surfaces.",
                },
            ],
        },
        process: {
            eyebrow: "06 — How We Work",
            title: "How We Work",
            subtitle: "A calm, four-step path from blank wall to finished mural.",
            steps: [
                {
                    n: "01",
                    title: "Discovery",
                    body: "Understanding the space, wall size, mood, and client vision.",
                },
                {
                    n: "02",
                    title: "Concept Design",
                    body: "Developing a visual direction, composition, and color story.",
                },
                {
                    n: "03",
                    title: "Proposal & Planning",
                    body: "Defining timing, budget, materials, and project scope.",
                },
                {
                    n: "04",
                    title: "Painting & Delivery",
                    body: "Professional mural execution and final touches on site.",
                },
            ],
        },
        clients: {
            eyebrow: "06 — Who I Work With",
            title: "Who I Work With",
            body:
                "From intimate interiors to large public-facing walls, each project is tailored to the character of the space.",
            list: [
                "Homes",
                "Cafés",
                "Restaurants",
                "Hotels",
                "Offices",
                "Stores / retail spaces",
                "Schools",
                "Public & cultural spaces",
            ],
        },
        testimonials: {
            eyebrow: "07 — Kind Words",
            items: [
                {
                    quote:
                        "Ahmet transformed our wall into the focal point of the entire space. The mural brought warmth, identity, and a truly memorable atmosphere.",
                    attr: "Boutique café owner, İstanbul",
                },
                {
                    quote:
                        "Working with Ahmet felt like collaborating with both an artist and a storyteller. The result is a wall our guests photograph every single day.",
                    attr: "Hotel art director, Aegean coast",
                },
                {
                    quote:
                        "Calm process, sharp vision, beautiful finish. Our office now feels like a place, not just a room.",
                    attr: "Creative studio founder, Ankara",
                },
            ],
        },
        pricing: {
            eyebrow: "05 — Pricing",
            title: "Pricing",
            intro:
                "Every mural is unique, which is why each project is priced individually. The final cost depends on the size of the wall, the complexity of the design, the surface condition, the location, and any preparation required.",
            range:
                "Murals start from €500, with most projects ranging between €800 and €3,000.",
            includedTitle: "Every project includes:",
            included: [
                "Initial consultation",
                "Custom design tailored to your space",
                "Professional hand-painted mural",
                "Protective finish (when required)",
            ],
            cta:
                "If you have a project in mind, I'd love to hear about it. Get in touch for a free, no-obligation quote.",
            ctaButton: "Get in Touch",
            note:
                "Looking for something smaller or larger? Every project is different, and I'm happy to create a quote that fits your vision and budget.",
            base:
                "Based in Amsterdam — available for projects across the Netherlands and internationally.",
        },
        contact: {
            eyebrow: "08 — Contact",
            title: "Let's create something",
            titleItalic: "for your wall.",
            cta:
                "Available for commissioned mural projects, wall painting, and artistic collaborations.",
            form: {
                name: "Name",
                email: "Email",
                phone: "Phone (optional)",
                project: "Project type",
                wall: "Wall size / location (optional)",
                message: "Tell me about your space",
                submit: "Send Inquiry",
                sending: "Sending…",
                success:
                    "Thank you — your message has reached the studio. I'll get back to you shortly.",
                error: "Something went wrong. Please try again or write directly via WhatsApp.",
                projects: [
                    "Home / private residence",
                    "Café / Restaurant",
                    "Hotel",
                    "Office",
                    "Retail / store",
                    "Public / cultural space",
                    "Other",
                ],
            },
            direct: {
                whatsapp: "WhatsApp",
                instagram: "Instagram",
                email: "Email",
            },
        },
        footer: {
            sitemap: "Sitemap",
            studio: "Studio",
            social: "Elsewhere",
            location: "Amsterdam, NL",
            rights: "All rights reserved.",
            credit: "Designed as a living portfolio.",
        },
    },
    tr: {
        nav: {
            home: "Ana Sayfa",
            about: "Hakkımda",
            work: "Çalışmalarım",
            offer: "Yaratıcı Hizmetler",
            pricing: "Fiyatlandırma",
            contact: "İletişim",
        },
        brand: {
            name: "Ahmet Şaşkın",
            tagline: "Duvar Sanatı & Mural",
        },
        hero: {
            kicker: "Mural Sanatçısı — seçilmiş portfolyo",
            line1: "Ruhu olan",
            line2Italic: "duvarlar.",
            line3: "",
            line4Italic: "",
            subhead:
                "Evler, kafeler, restoranlar, oteller, ofisler ve kamusal alanlar için özel mural sanatı, duvar boyama ve görsel hikâye anlatımı.",
            intro:
                "Ahmet Şaşkın, her mekâna kimlik, duygu ve atmosfer katan el yapımı duvar sanatı ve mural çalışmaları üretir.",
            ctaPrimary: "Çalışmalarımı Gör",
            ctaSecondary: "İletişime Geç",
            chips: [
                "Özel muraller",
                "Tuval resmi",
                "İç & dış mekân",
                "Sipariş üzerine eser",
                "Özel & ticari projeler",
            ],
            scroll: "Keşfe başlamak için kaydırın",
        },
        marquee: [
            "El yapımı",
            "Özel muraller",
            "İç & dış mekân",
            "Ticari projeler",
            "Editöryel duvar sanatı",
            "Konseptten uygulamaya",
        ],
        about: {
            eyebrow: "01 — Hakkımda",
            title: "Ahmet Şaşkın Hakkında",
            body:
                "Ahmet Şaşkın; Amsterdam merkezli bir yaratıcı profesyonel, mural sanatçısı ve görsel üreticidir — el yapımı muraller ve özel duvar sanatı üzerine çalışan Ahmetography'nin kurucusu. Online ödemeler sektöründeki uzun kariyerinin ardından, 2023 yılında karşısına çıkan mükemmel fırsatla birlikte gerçek tutkusu olan sanata yönelmeye karar verdi.",
            body2:
                "O günden beri Ahmet, farklı boyama tekniklerini, malzemelerini ve stillerini kendi kendine çalışarak geliştirmeye devam ediyor — özellikle mural tasarımı ve görsel hikâye anlatımı üzerine odaklanarak. Bu yolculuk, 2010–2011 yıllarında Gerrit Rietveld Akademisi'nde aldığı sanat eğitimiyle daha erken bir dönemde şekillenmişti.\n\nÖzel bir konut olsun ya da kafe, restoran, ofis, otel veya dış mekân gibi ticari bir alan olsun; Ahmet, her müşterinin vizyonuna göre özel tasarlanmış muraller üretir. Mevcut bir iç mekânı tamamlayan tasarımlar geliştirebilir ya da belirli bir fikri veya stili hayata geçirerek mekânın karakterini yansıtan özgün bir eser ortaya koyabilir. Onun için bir mural yalnızca bir dekorasyondan ibaret değildir — mekânı dönüştürme, duygu uyandırma ve o mekânı deneyimleyen insanlarla kalıcı bir bağ kurma fırsatıdır.",
            quote:
                "Her duvarın bir hikâyesi vardır. Gelin, sizinkini birlikte boyayalım.",
            quoteAttr: "— Ahmet Şaşkın",
            metaLeft: "Çalışıyor",
            metaLeftValue: "2024'ten beri",
            metaRight: "Merkez",
            metaRightValue: "Amsterdam, NL",
        },
        stats: {
            eyebrow: "02 — Rakamlarla",
            items: [
                { num: "20+", label: "Tamamlanmış duvar" },
                { num: "%100", label: "El yapımı" },
                { num: "—", label: "İç & dış mekân" },
            ],
            footnote:
                "Türkiye genelinde özel ve ticari komisyonlar; küçük iç mekânlardan büyük ölçekli kamusal duvarlara.",
        },
        portfolio: {
            eyebrow: "03 — Seçilmiş Eserler",
            title: "Çalışmalarım",
            subtitle:
                "Yakın dönemde tamamlanan muraller, boyanmış duvarlar ve sipariş üzerine kompozisyonlardan derlenmiş bir arşiv.",
            featuredLabel: "Öne çıkan",
            seeAll: "Tüm Çalışmalar",
            filters: {
                all: "Tümü",
                mural: "Muraller",
                canvas: "Tuval resimleri",
            },
            cardMeta: {
                location: "Konum",
                surface: "Yüzey",
                size: "Ölçüler",
                medium: "Teknik",
            },
        },
        services: {
            eyebrow: "04 — Yaratıcı Hizmetler",
            title: "Yaratıcı Hizmetler",
            subtitle:
                "El yapımı sanatı mekânınıza taşımanın altı yolu — hem özel konutlar hem de kafe, restoran, otel, ofis gibi ticari mekânlar için.",
            items: [
                {
                    title: "Özel Muraller",
                    body: "Duvarınıza, hikâyenize ve mekânınıza özel olarak tasarlanmış, el yapımı muraller.",
                },
                {
                    title: "Tuval Resmi",
                    body: "El yapımı özgün tuval eserleri — çeşitli ebatlarda; vurgu niteliğindeki parçalardan büyük ölçekli komisyonlara kadar.",
                },
                {
                    title: "Mekâna Özel Enstalasyonlar",
                    body: "Bir mekânın mimarisi ve atmosferi için özel olarak yaratılan eserler — her parçanın entegre ve benzersiz hissettirmesini sağlar.",
                },
                {
                    title: "Dekoratif Duvar Sanatı",
                    body: "Ağırlama ve perakende iç mekânlarını yükselten katmanlı desenler, dokular ve motifler.",
                },
                {
                    title: "Ticari Mural ve Sanat Projeleri",
                    body: "Ağırlama, perakende, ofis, kafe ve ayırt edici bir eser arayan diğer ticari ortamlar için yaratıcı çözümler.",
                },
                {
                    title: "Yaratıcı Danışmanlık",
                    body: "İç ve dış yüzeyler için konsept geliştirme, renk yönü, yerleşim önerileri ve sanatsal planlama.",
                },
            ],
        },
        process: {
            eyebrow: "06 — Nasıl Çalışıyoruz",
            title: "Nasıl Çalışıyoruz",
            subtitle:
                "Boş duvardan tamamlanmış murale dört adımlık sakin bir yol.",
            steps: [
                {
                    n: "01",
                    title: "Keşif",
                    body: "Mekânı, duvar ölçüsünü, ruh halini ve müşterinin vizyonunu anlamak.",
                },
                {
                    n: "02",
                    title: "Konsept Tasarım",
                    body: "Görsel yönü, kompozisyonu ve renk hikâyesini geliştirmek.",
                },
                {
                    n: "03",
                    title: "Teklif & Planlama",
                    body: "Zamanlama, bütçe, malzemeler ve proje kapsamını netleştirmek.",
                },
                {
                    n: "04",
                    title: "Boyama & Teslim",
                    body: "Profesyonel mural uygulaması ve mekânda son rötuşlar.",
                },
            ],
        },
        clients: {
            eyebrow: "06 — Kimlerle Çalışıyorum",
            title: "Kimlerle Çalışıyorum",
            body:
                "Küçük iç mekânlardan büyük kamusal duvarlara kadar her proje, mekânın karakterine göre özel olarak tasarlanır.",
            list: [
                "Evler",
                "Kafeler",
                "Restoranlar",
                "Oteller",
                "Ofisler",
                "Mağazalar / perakende",
                "Okullar",
                "Kamusal & kültürel alanlar",
            ],
        },
        testimonials: {
            eyebrow: "07 — Müşteri Yorumları",
            items: [
                {
                    quote:
                        "Ahmet duvarımızı tüm mekânın odak noktasına dönüştürdü. Mural; sıcaklık, kimlik ve gerçekten akılda kalıcı bir atmosfer getirdi.",
                    attr: "Butik kafe sahibi, İstanbul",
                },
                {
                    quote:
                        "Ahmet ile çalışmak hem bir sanatçı hem bir hikâye anlatıcısıyla iş birliği yapmak gibiydi. Sonuç, misafirlerimizin her gün fotoğrafladığı bir duvar.",
                    attr: "Otel sanat yönetmeni, Ege",
                },
                {
                    quote:
                        "Sakin bir süreç, net bir vizyon, zarif bir bitiriş. Ofisimiz artık sadece bir oda değil, bir yer gibi hissettiriyor.",
                    attr: "Yaratıcı stüdyo kurucusu, Ankara",
                },
            ],
        },
        pricing: {
            eyebrow: "05 — Fiyatlandırma",
            title: "Fiyatlandırma",
            intro:
                "Her mural benzersizdir; bu yüzden her proje ayrı ayrı fiyatlandırılır. Nihai ücret; duvarın boyutuna, tasarımın karmaşıklığına, yüzeyin durumuna, konuma ve gereken hazırlık işlerine göre değişir.",
            range:
                "Muraller €500'dan başlar; projelerin çoğu €800 ile €3.000 arasındadır.",
            includedTitle: "Her proje şunları içerir:",
            included: [
                "İlk görüşme ve keşif",
                "Mekânınıza özel tasarım",
                "Profesyonel el yapımı mural",
                "Koruyucu vernik / bitiriş (gerektiğinde)",
            ],
            cta:
                "Aklınızda bir proje varsa duymak isterim. Ücretsiz ve bağlayıcı olmayan bir teklif için bana yazın.",
            ctaButton: "İletişime Geç",
            note:
                "Daha küçük veya daha büyük bir iş mi arıyorsunuz? Her proje farklıdır; vizyonunuza ve bütçenize uygun bir teklif hazırlamaktan mutluluk duyarım.",
            base:
                "Amsterdam merkezli — Hollanda geneli ve uluslararası projeler için müsaitim.",
        },
        contact: {
            eyebrow: "08 — İletişim",
            title: "Duvarınız için birlikte",
            titleItalic: "bir şey üretelim.",
            cta:
                "Sipariş üzerine mural projeleri, duvar boyama ve sanatsal iş birlikleri için müsaitim.",
            form: {
                name: "İsim",
                email: "E-posta",
                phone: "Telefon (opsiyonel)",
                project: "Proje türü",
                wall: "Duvar ölçüsü / konum (opsiyonel)",
                message: "Mekânınızdan bahsedin",
                submit: "Mesaj Gönder",
                sending: "Gönderiliyor…",
                success:
                    "Teşekkürler — mesajınız stüdyoya ulaştı. En kısa sürede dönüş yapacağım.",
                error: "Bir şeyler ters gitti. Lütfen tekrar deneyin veya doğrudan WhatsApp üzerinden yazın.",
                projects: [
                    "Ev / özel konut",
                    "Kafe / Restoran",
                    "Otel",
                    "Ofis",
                    "Mağaza / perakende",
                    "Kamusal / kültürel alan",
                    "Diğer",
                ],
            },
            direct: {
                whatsapp: "WhatsApp",
                instagram: "Instagram",
                email: "E-posta",
            },
        },
        footer: {
            sitemap: "Site haritası",
            studio: "Stüdyo",
            social: "Sosyal",
            location: "Amsterdam, Hollanda",
            rights: "Tüm hakları saklıdır.",
            credit: "Yaşayan bir portfolyo olarak tasarlandı.",
        },
    },
    nl: {
        nav: {
            home: "Home",
            about: "Over",
            work: "Mijn Werk",
            offer: "Creatieve Diensten",
            pricing: "Prijzen",
            contact: "Contact",
        },
        brand: {
            name: "Ahmet Saskin",
            tagline: "Muurkunst & Ambacht",
        },
        hero: {
            kicker: "Muralist — geselecteerd portfolio",
            line1: "Muren met",
            line2Italic: "ziel.",
            line3: "",
            line4Italic: "",
            subhead:
                "Op maat geschilderde muurkunst, muurschilderingen en visuele verhalen voor woningen, cafés, restaurants, hotels, kantoren en publieke ruimtes.",
            intro:
                "Ahmet Saskin creëert handgeschilderde muurkunst en muurschilderingen die identiteit, emotie en sfeer in elke ruimte brengen.",
            ctaPrimary: "Bekijk Mijn Werk",
            ctaSecondary: "Neem Contact Op",
            chips: [
                "Op maat",
                "Doekschilderingen",
                "Binnen & buiten",
                "In opdracht",
                "Privé & commercieel",
            ],
            scroll: "Scroll om te beginnen",
        },
        marquee: [
            "Handgeschilderd",
            "Op maat",
            "Binnen & buiten",
            "Commerciële opdrachten",
            "Redactionele muurkunst",
            "Concept tot uitvoering",
        ],
        about: {
            eyebrow: "01 — Over",
            title: "Over Ahmet Saskin",
            body:
                "Ahmet Saskin is een creatieve professional, muralist en visueel maker, gevestigd in Amsterdam — de oprichter van Ahmetography, gespecialiseerd in handgeschilderde muurschilderingen en muurkunst op maat. Na een lange carrière in de online betalingssector diende in 2023 de perfecte kans zich aan, en besloot hij zijn ware passie voor de kunst te volgen.",
            body2:
                "Sindsdien ontwikkelt Ahmet zijn vak zelfstandig verder — door zelfstudie van verschillende schildertechnieken, materialen en stijlen, met een focus op muurschildering en visuele verhalen. Die reis werd eerder mede vormgegeven door een kunstopleiding aan de Gerrit Rietveld Academie in 2010–2011.\n\nOf het nu gaat om een particuliere woning of een commerciële ruimte — een café, restaurant, kantoor, hotel of buitenlocatie — Ahmet maakt muurschilderingen op maat, afgestemd op de visie van elke opdrachtgever. Hij kan een ontwerp maken dat een bestaand interieur aanvult, of een specifiek idee tot leven brengen als een uniek werk dat het karakter van de ruimte weerspiegelt. Voor hem is een muurschildering meer dan decoratie — het is een kans om een ruimte te transformeren, emotie op te roepen en een blijvende verbinding te creëren met de mensen die haar beleven.",
            quote:
                "Elke muur heeft een verhaal. Laten we die van jou samen schilderen.",
            quoteAttr: "— Ahmet Saskin",
            metaLeft: "Werkzaam sinds",
            metaLeftValue: "2024",
            metaRight: "Gevestigd in",
            metaRightValue: "Amsterdam, NL",
        },
        stats: {
            eyebrow: "02 — In Cijfers",
            items: [
                { num: "20+", label: "Voltooide muren" },
                { num: "100%", label: "Handgeschilderd" },
                { num: "—", label: "Binnen & buiten" },
            ],
            footnote:
                "Particuliere en commerciële opdrachten door heel Türkiye, van intieme interieurs tot grootschalige publieke wanden.",
        },
        portfolio: {
            eyebrow: "03 — Geselecteerd Werk",
            title: "Mijn Werk",
            subtitle:
                "Een gecureerd archief van recente muurschilderingen, geschilderde muren en composities in opdracht.",
            featuredLabel: "Uitgelicht",
            seeAll: "Bekijk Al Het Werk",
            filters: {
                all: "Alles",
                mural: "Muurschilderingen",
                canvas: "Doekschilderingen",
            },
            cardMeta: {
                location: "Locatie",
                surface: "Oppervlak",
                size: "Afmetingen",
                medium: "Techniek",
            },
        },
        services: {
            eyebrow: "04 — Creatieve Diensten",
            title: "Creatieve Diensten",
            subtitle:
                "Zes manieren om handgeschilderde kunst in uw ruimte te brengen — voor zowel particuliere woningen als commerciële locaties: cafés, restaurants, hotels, kantoren en meer.",
            items: [
                {
                    title: "Muurschilderingen op Maat",
                    body: "Handgeschilderde muurschilderingen, ontworpen specifiek voor uw muur, uw verhaal en uw ruimte.",
                },
                {
                    title: "Doekschilderingen",
                    body: "Originele handgeschilderde werken op doek, verkrijgbaar in verschillende formaten — van accentstukken tot grootschalige opdrachten.",
                },
                {
                    title: "Locatiespecifieke Installaties",
                    body: "Kunstwerken speciaal gecreëerd voor de architectuur en sfeer van een ruimte — zodat elk werk zich geïntegreerd en uniek aanvoelt.",
                },
                {
                    title: "Decoratieve Muurkunst",
                    body: "Gelaagde patronen, texturen en motieven die hospitality- en retail-interieurs verheffen.",
                },
                {
                    title: "Commerciële Muur- en Kunstprojecten",
                    body: "Creatieve oplossingen voor horeca, retail, kantoren, cafés en andere commerciële omgevingen die op zoek zijn naar onderscheidende kunst.",
                },
                {
                    title: "Creatief Advies",
                    body: "Conceptontwikkeling, kleurrichting, plaatsingsideeën en artistieke planning voor binnen- en buitenoppervlakken.",
                },
            ],
        },
        process: {
            eyebrow: "06 — Zo Werken We",
            title: "Zo Werken We",
            subtitle:
                "Een rustig pad in vier stappen, van een blanke muur naar een voltooide muurschildering.",
            steps: [
                {
                    n: "01",
                    title: "Ontdekking",
                    body: "De ruimte, muurgrootte, sfeer en visie van de opdrachtgever begrijpen.",
                },
                {
                    n: "02",
                    title: "Conceptontwerp",
                    body: "Een visuele richting, compositie en kleurverhaal ontwikkelen.",
                },
                {
                    n: "03",
                    title: "Voorstel & Planning",
                    body: "Planning, budget, materialen en projectomvang vastleggen.",
                },
                {
                    n: "04",
                    title: "Schilderen & Oplevering",
                    body: "Professionele uitvoering en finishing touches ter plaatse.",
                },
            ],
        },
        clients: {
            eyebrow: "06 — Voor Wie Ik Werk",
            title: "Voor Wie Ik Werk",
            body:
                "Van intieme interieurs tot grote publieke muren, elk project is afgestemd op het karakter van de ruimte.",
            list: [
                "Woningen",
                "Cafés",
                "Restaurants",
                "Hotels",
                "Kantoren",
                "Winkels / retail",
                "Scholen",
                "Publieke & culturele ruimtes",
            ],
        },
        testimonials: {
            eyebrow: "07 — Mooie Woorden",
            items: [
                {
                    quote:
                        "Ahmet veranderde onze muur in het middelpunt van de hele ruimte. De muurschildering bracht warmte, identiteit en een werkelijk onvergetelijke sfeer.",
                    attr: "Boutique-café eigenaar, İstanbul",
                },
                {
                    quote:
                        "Met Ahmet werken voelde als samenwerken met zowel een kunstenaar als een verteller. Het resultaat is een muur die onze gasten elke dag fotograferen.",
                    attr: "Hoteldirecteur kunst, Egeïsche kust",
                },
                {
                    quote:
                        "Rustig proces, scherpe visie, prachtige afwerking. Ons kantoor voelt nu als een plek, niet zomaar een ruimte.",
                    attr: "Oprichter creatieve studio, Ankara",
                },
            ],
        },
        contact: {
            eyebrow: "08 — Contact",
            title: "Laten we iets creëren",
            titleItalic: "voor uw muur.",
            cta:
                "Beschikbaar voor muurschilderingen in opdracht, muurbeschildering en artistieke samenwerkingen.",
            form: {
                name: "Naam",
                email: "E-mail",
                phone: "Telefoon (optioneel)",
                project: "Type project",
                wall: "Muurgrootte / locatie (optioneel)",
                message: "Vertel me over uw ruimte",
                submit: "Aanvraag Versturen",
                sending: "Versturen…",
                success:
                    "Dank u — uw bericht is in de studio aangekomen. Ik neem spoedig contact met u op.",
                error:
                    "Er ging iets mis. Probeer het opnieuw of schrijf direct via WhatsApp.",
                projects: [
                    "Woning / particulier",
                    "Café / Restaurant",
                    "Hotel",
                    "Kantoor",
                    "Winkel / retail",
                    "Publieke / culturele ruimte",
                    "Anders",
                ],
            },
            direct: {
                whatsapp: "WhatsApp",
                instagram: "Instagram",
                email: "E-mail",
            },
        },
        footer: {
            sitemap: "Sitemap",
            studio: "Studio",
            social: "Elders",
            location: "Amsterdam, NL",
            rights: "Alle rechten voorbehouden.",
            credit: "Ontworpen als een levend portfolio.",
        },
    },
};

export const defaultLang = "en";
